import { ElementRef, Injectable, SimpleChange, Type } from '@angular/core';

import { ElTrigger, ElTriggerStrategy, ElTriggerStrategyBuilderService } from '../overlay-trigger';
import {
  ElAdjustableConnectedPositionStrategy,
  ElAdjustment,
  ElPosition,
  ElPositionBuilderService,
} from '../overlay-position';
import { ElRenderableContainer } from '../overlay-container';
import { ElOverlayContent } from '../overlay-service';
import { ElDynamicOverlay } from './dynamic-overlay';
import { ElOverlayConfig } from '../mapping';

export class ElDynamicOverlayChange extends SimpleChange {

  constructor(previousValue: any, currentValue: any, firstChange: boolean = false) {
    super(previousValue, currentValue, firstChange);
  }

  isChanged(): boolean {
    return this.currentValue !== this.previousValue;
  }
}

@Injectable()
export class ElDynamicOverlayHandler {

  protected _componentType: Type<ElRenderableContainer>;
  protected _host: ElementRef;
  protected _context: Object = {};
  protected _content: ElOverlayContent;
  protected _trigger: ElTrigger = ElTrigger.NOOP;
  protected _position: ElPosition = ElPosition.TOP;
  protected _adjustment: ElAdjustment = ElAdjustment.NOOP;
  protected _offset: number = 15;
  protected _overlayConfig: ElOverlayConfig = {};

  protected dynamicOverlay: ElDynamicOverlay;
  protected triggerStrategy: ElTriggerStrategy;

  protected positionStrategy: ElAdjustableConnectedPositionStrategy;

  protected changes: { [key: string]: ElDynamicOverlayChange } = {};

  constructor(private positionBuilder: ElPositionBuilderService,
              private triggerStrategyBuilder: ElTriggerStrategyBuilderService,
              private dynamicOverlayService: ElDynamicOverlay) {
  }

  host(host: ElementRef) {
    this.changes.host = new ElDynamicOverlayChange(this._host, host);
    this._host = host;
    return this;
  }

  trigger(trigger: ElTrigger) {
    this.changes.trigger = new ElDynamicOverlayChange(this._trigger, trigger);
    this._trigger = trigger;
    return this;
  }

  position(position: ElPosition) {
    this.changes.position = new ElDynamicOverlayChange(this._position, position);
    this._position = position;
    return this;
  }

  adjustment(adjustment: ElAdjustment) {
    this.changes.adjustment = new ElDynamicOverlayChange(this._adjustment, adjustment);
    this._adjustment = adjustment;
    return this;
  }

  componentType(componentType: Type<ElRenderableContainer>) {
    this.changes.componentType = new ElDynamicOverlayChange(this._componentType, componentType);
    this._componentType = componentType;
    return this;
  }

  content(content: ElOverlayContent) {
    this.changes.content = new ElDynamicOverlayChange(this._content, content);
    this._content = content;
    return this;
  }

  context(context: {}) {
    this.changes.context = new ElDynamicOverlayChange(this._context, context);
    this._context = context;
    return this;
  }

  offset(offset: number) {
    this.changes.offset = new ElDynamicOverlayChange(this._offset, offset);
    this._offset = offset;
    return this;
  }

  overlayConfig(overlayConfig: ElOverlayConfig) {
    this.changes.overlayConfig = new ElDynamicOverlayChange(this._overlayConfig, overlayConfig);
    this._overlayConfig = overlayConfig;
    return this;
  }

  build() {
    if (!this._componentType || !this._host) {
      throw Error(`ElDynamicOverlayHandler: at least 'componentType' and 'host' should be
      passed before building a dynamic overlay.`)
    }
    this.dynamicOverlay = this.dynamicOverlayService.create(
      this._componentType,
      this._content,
      this._context,
      this.createPositionStrategy(),
      this._overlayConfig,
    );

    this.connect();
    this.clearChanges();

    return this.dynamicOverlay;
  }

  rebuild() {
    /**
     * we should not throw here
     * as we use rebuilt in lifecycle hooks
     * which it could be called before the build
     * so we just ignore this call
     */
    if (!this.dynamicOverlay) {
      return;
    }

    if (this.isPositionStrategyUpdateRequired()) {
      this.dynamicOverlay.setPositionStrategy(this.createPositionStrategy());
    }

    if (this.isTriggerStrategyUpdateRequired()) {
      this.connect();
    }

    if (this.isContainerRerenderRequired()) {
      this.dynamicOverlay.setContentAndContext(this._content, this._context);
    }

    if (this.isComponentTypeUpdateRequired()) {
      this.dynamicOverlay.setComponent(this._componentType);
    }

    if (this.isOverlayConfigUpdateRequired()) {
      this.dynamicOverlay.setOverlayConfig(this._overlayConfig);
    }

    this.clearChanges();
    return this.dynamicOverlay;
  }

  connect() {
    if (!this.dynamicOverlay) {
      throw new Error(`ElDynamicOverlayHandler: cannot connect to DynamicOverlay
      as it is not created yet. Call build() first`);
    }
    this.disconnect();
    this.subscribeOnTriggers(this.dynamicOverlay);
  }

  disconnect() {
    if (this.triggerStrategy) {
      this.triggerStrategy.destroy();
    }
  }

  destroy() {
    this.disconnect();
    this.clearChanges();
    if (this.dynamicOverlay) {
      this.dynamicOverlay.dispose();
    }
  }

  protected createPositionStrategy() {
    return this.positionBuilder
      .connectedTo(this._host)
      .position(this._position)
      .adjustment(this._adjustment)
      .offset(this._offset);
  }

  protected subscribeOnTriggers(dynamicOverlay: ElDynamicOverlay) {
    this.triggerStrategy = this.triggerStrategyBuilder
      .trigger(this._trigger)
      .host(this._host.nativeElement)
      .container(() => dynamicOverlay.getContainer())
      .build();

    this.triggerStrategy.show$.subscribe(() => dynamicOverlay.show());
    this.triggerStrategy.hide$.subscribe(() => dynamicOverlay.hide());
  }

  protected isContainerRerenderRequired() {
    return this.isContentUpdated()
      || this.isContextUpdated()
      || this.isPositionStrategyUpdateRequired();
  }

  protected isPositionStrategyUpdateRequired(): boolean {
    return this.isAdjustmentUpdated() || this.isPositionUpdated() || this.isOffsetUpdated() || this.isHostUpdated();
  }

  protected isTriggerStrategyUpdateRequired(): boolean {
    return this.isTriggerUpdated() || this.isHostUpdated();
  }

  protected isComponentTypeUpdateRequired(): boolean {
    return this.isComponentTypeUpdated();
  }

  private isOverlayConfigUpdateRequired(): boolean {
    return this.isOverlayConfigUpdated();
  }

  protected isComponentTypeUpdated(): boolean {
    return this.changes.componentType && this.changes.componentType.isChanged();
  }

  protected isContentUpdated(): boolean {
    return this.changes.content && this.changes.content.isChanged();
  }

  protected isContextUpdated(): boolean {
    return this.changes.context && this.changes.context.isChanged();
  }

  protected isAdjustmentUpdated(): boolean {
    return this.changes.adjustment && this.changes.adjustment.isChanged();
  }

  protected isPositionUpdated(): boolean {
    return this.changes.position && this.changes.position.isChanged();
  }

  protected isHostUpdated(): boolean {
    return this.changes.host && this.changes.host.isChanged();
  }

  protected isTriggerUpdated(): boolean {
    return this.changes.trigger && this.changes.trigger.isChanged();
  }

  protected isOffsetUpdated(): boolean {
    return this.changes.offset && this.changes.offset.isChanged();
  }

  protected isOverlayConfigUpdated(): boolean {
    return this.changes.overlayConfig && this.changes.overlayConfig.isChanged();
  }

  protected clearChanges() {
    this.changes = {};
  }
}
